import type {
  FetchMatcher,
  JsonApiDoc,
  JsonApiResource,
  MatcherFixture,
} from 'tio-data/demo/types';
import type { GeneratePaymentPlanPayload } from 'tio-data/requests/payment-plan-explorer';
import {
  buildSortPlansPayloads,
  isCurrentPlanRow,
  loanInputsFromSummary,
  parseResultSections,
  type SortPlansPayload,
  type SortedPlanRow,
} from 'tio-common/utils/strategy-finder-0726';
import {
  getDemoDebtLevel,
  isFirstLoginPersona,
  isPslfEligibleClient,
  type DemoSessionSelectionSnapshot,
} from 'tio-employee/utils/career-pathways-demo-data';
import { USER_ID } from 'tio-employee/demo/identity-fixtures';
import { requestingAppIs } from 'tio-employee/demo/types';
import {
  buildDemoLoanSummary,
  DEMO_PSLF_QUALIFYING_PAYMENTS,
  demoHousehold,
  demoPslfActive,
  type DemoHousehold,
} from 'tio-employee/demo/loan-profile';
import {
  buildSortPlansOutput,
  PLAN_TITLES,
  simulateSchedule,
} from 'tio-employee/demo/repayment-plan-simulator';
import { buildIdr0726Payloads } from 'tio-employee/utils/idr-dashboard-0726';
import { buildFe0726Payloads } from 'tio-employee/utils/forgiveness-estimator-0726';

export const DEMO_LOAN_REC_IDS = {
  idr0726Latest: 'demo-loan-rec-idr-0726-2',
  idr0726Earlier: 'demo-loan-rec-idr-0726-1',
  idrLegacy: 'demo-loan-rec-idr-legacy-1',
  sf0726: 'demo-loan-rec-sf-0726-1',
  pslf0726: 'demo-loan-rec-pslf-0726-1',
  pslfLegacy: 'demo-loan-rec-pslf-legacy-1',
};

const PSLF_0726_CREATED_AT = '2026-06-15T14:00:00.000Z';
const PSLF_RUN_CREATED_AT = '2026-09-02T15:00:00.000Z';
const PSLF_LEGACY_CREATED_AT = '2026-05-20T14:00:00.000Z';

const ECHO_SLUGS: Record<string, string> = {
  'IDR-0726': 'idr-0726',
  'SF-0726': 'sf-0726',
  'PSLF-0726': 'pslf-0726',
};

const SUMMARY_URL_RE = new RegExp(`/users/${USER_ID}/summarize-loans(?:\\?|$)`);
const GENERATE_URL_RE = /\/api\/v2\/payment-plan-explorer\/generate(?:\?|$)/;
const SINGLE_URL_RE = /\/recommendations\/(demo-loan-rec-[a-z0-9-]+)(?:\?|$)/;
const COLLECTION_URL_RE = /\/recommendations\?/;
const CREATE_URL_RE = /\/recommendations(?:\?|$)/;
const ANSWER_PROFILES_URL_RE = /\/answer-profiles\?/;
const DEMO_USER_FILTER_RE = new RegExp(`filter(?:%5B|\\[)user(?:%5D|\\])=${USER_ID}(?:&|$)`);
const RUN_ID_RE = /^demo-loan-rec-run-(idr-0726|sf-0726|pslf-0726)-/;

const isDemoUserFilter = (url: string) => DEMO_USER_FILTER_RE.test(url);

let submitSeq = 0;

// A PSLF session's borrower is on IBR; every other session keeps the debt level's plan.
export function demoSessionLoanSummary(
  selection: DemoSessionSelectionSnapshot,
  debtLevelId = selection.debtLevelId
) {
  return buildDemoLoanSummary(debtLevelId, demoPslfActive(selection));
}

function sessionHousehold(
  selection: DemoSessionSelectionSnapshot,
  debtLevelId = selection.debtLevelId
): DemoHousehold {
  const household = demoHousehold(debtLevelId);
  return demoPslfActive(selection)
    ? { ...household, madePayments: DEMO_PSLF_QUALIFYING_PAYMENTS }
    : household;
}

export function demoPslfHistoryVisible(selection: DemoSessionSelectionSnapshot) {
  return (
    demoPslfActive(selection) &&
    selection.pslfScenarioId === 'full-journey' &&
    demoLoanHistoryVisible(selection)
  );
}

export function demoLoanHistoryVisible(selection: DemoSessionSelectionSnapshot) {
  return (
    getDemoDebtLevel(selection.debtLevelId).hasLinkedLoans &&
    !isFirstLoginPersona(selection.userScenarioId)
  );
}

// Saved runs always show a linked loan, so a not-linked session reads them at the medium level.
function recordProfile(selection: DemoSessionSelectionSnapshot) {
  const level = getDemoDebtLevel(selection.debtLevelId).hasLinkedLoans
    ? selection.debtLevelId
    : 'medium';
  return {
    household: sessionHousehold(selection, level),
    loanInputs: loanInputsFromSummary(demoSessionLoanSummary(selection, level)),
  };
}

function idrModel(household: DemoHousehold, agi = household.agiCents) {
  return {
    agi,
    numberOfAdults: household.numberOfAdults,
    numberOfChildren: household.numberOfChildren,
    firstLoanTime: household.firstLoanTime,
    lastLoanTime: household.lastLoanTime,
    madePayments: household.madePayments,
    affordPayments: household.affordPayments,
  };
}

function calculation(data: SortPlansPayload[], requestingApp: string) {
  return {
    calculationInput: { data },
    calculationOutput: { data: data.map((row) => buildSortPlansOutput(row, requestingApp)) },
  };
}

function recommendation(id: string, attributes: Record<string, unknown>): JsonApiResource {
  return { type: 'recommendation', id, attributes: { hasErrorInCalcs: false, ...attributes } };
}

function idr0726Record(selection: DemoSessionSelectionSnapshot, earlier: boolean) {
  const { household, loanInputs } = recordProfile(selection);
  const agi = earlier ? Math.round((household.agiCents * 0.94) / 100) * 100 : household.agiCents;
  return recommendation(
    earlier ? DEMO_LOAN_REC_IDS.idr0726Earlier : DEMO_LOAN_REC_IDS.idr0726Latest,
    {
      requestingApp: 'IDR-0726',
      createdAt: earlier ? '2026-07-14T15:00:00.000Z' : '2026-09-02T15:00:00.000Z',
      ...calculation(buildIdr0726Payloads(idrModel(household, agi), loanInputs), 'IDR-0726'),
    }
  );
}

const legacySummary = (row: SortedPlanRow, balanceCents: number) => ({
  'payoff-amount': [row.total_payment],
  'term-months': [row.num_payments],
  'monthly-payments': [row.first_month_payment],
  'total-interest-paid': [row.total_payment + row.forgiveness_paid - balanceCents],
  'estimated-amount-forgiven': [row.forgiveness_paid],
});

function idrLegacyRecord(selection: DemoSessionSelectionSnapshot) {
  const earlier = idr0726Record(selection, true).attributes ?? {};
  const input = (earlier['calculationInput'] as { data: SortPlansPayload[] }).data[0];
  const plans =
    (earlier['calculationOutput'] as { data: Array<{ plans: SortedPlanRow[] }> }).data[0]?.plans ??
    [];
  const best = plans[0];
  const current = plans.find((row) => row.plan === 'tenYearsStandard');
  const balance = input?.current_balance ?? 0;
  const incomeDriven = best && best.plan !== 'tenYearsStandard';
  return recommendation(DEMO_LOAN_REC_IDS.idrLegacy, {
    requestingApp: 'IDR',
    createdAt: '2025-10-21T15:00:00.000Z',
    bestPlanSummary: best ? legacySummary(best, balance) : null,
    currentPlanSummary: current ? legacySummary(current, balance) : null,
    bestPlanTableHeadings: best
      ? [
          {
            title: PLAN_TITLES[best.plan] ?? best.plan,
            bullets: incomeDriven
              ? [
                  'Payments based on your income and family size',
                  'Any remaining balance is forgiven at the end of the term',
                ]
              : ['Fixed monthly payments', 'Paid off by the end of the term'],
          },
        ]
      : [],
    currentPlanTableHeadings: [
      { title: 'Standard Repayment', bullets: ['Fixed payments over 10 years'] },
    ],
    bestPlanNextSteps: [],
    inputSummary: {},
    outputSummary: {},
  });
}

function sf0726Record(selection: DemoSessionSelectionSnapshot) {
  const { household, loanInputs } = recordProfile(selection);
  const isGov = isPslfEligibleClient(selection.clientId);
  const model = {
    payoffGoal: 'slow',
    affordPayments: household.affordPayments,
    agi: household.agiCents,
    numberOfAdults: household.numberOfAdults,
    numberOfChildren: household.numberOfChildren,
    firstLoanTime: household.firstLoanTime,
    lastLoanTime: household.lastLoanTime,
    residenceState: 'OH',
    currentlyWorkingToward: 'no-but-want-learn',
    currentForgivenessProgram: '',
    madePayments: household.madePayments,
    isGov,
    isTeacher: false,
    isOther: !isGov,
    planInGov: isGov ? 'for-10-years-more' : '',
    planAsTeacher: '',
    employmentMonths: 0,
    tlfQualifies17500: false,
  };
  return recommendation(DEMO_LOAN_REC_IDS.sf0726, {
    requestingApp: 'SF-0726',
    createdAt: '2026-08-18T15:00:00.000Z',
    ...calculation(buildSortPlansPayloads(model, loanInputs), 'SF-0726'),
  });
}

function pslf0726Calculation(selection: DemoSessionSelectionSnapshot, earlier = false) {
  const { household, loanInputs } = recordProfile(selection);
  const agi = earlier ? Math.round((household.agiCents * 0.94) / 100) * 100 : household.agiCents;
  return calculation(buildFe0726Payloads(idrModel(household, agi), loanInputs), 'PSLF-0726');
}

function pslf0726Record(
  selection: DemoSessionSelectionSnapshot,
  id: string,
  createdAt = PSLF_RUN_CREATED_AT
) {
  return recommendation(id, {
    requestingApp: 'PSLF-0726',
    createdAt,
    ...pslf0726Calculation(selection),
  });
}

// The legacy estimator's plan summaries, taken from an earlier run of the PSLF plan set.
function pslfLegacyRecord(selection: DemoSessionSelectionSnapshot) {
  const { calculationInput, calculationOutput } = pslf0726Calculation(selection, true);
  const [section] = parseResultSections(calculationInput, calculationOutput);
  const best = section?.plans[0];
  const current = section?.plans.find((row) => isCurrentPlanRow(row, section));
  const balance = section?.input?.current_balance ?? 0;
  return recommendation(DEMO_LOAN_REC_IDS.pslfLegacy, {
    requestingApp: 'PSLF',
    createdAt: PSLF_LEGACY_CREATED_AT,
    bestPlanSummary: best ? legacySummary(best, balance) : null,
    currentPlanSummary: current ? legacySummary(current, balance) : null,
    bestPlanNextSteps: [],
    bestPlanTableHeadings: [],
    currentPlanTableHeadings: [],
    inputSummary: {},
    outputSummary: {},
  });
}

export function buildDemoLoanRecommendations(selection: DemoSessionSelectionSnapshot) {
  return {
    idr0726Latest: idr0726Record(selection, false),
    idr0726Earlier: idr0726Record(selection, true),
    idrLegacy: idrLegacyRecord(selection),
    sf0726: sf0726Record(selection),
    pslf0726: pslf0726Record(selection, DEMO_LOAN_REC_IDS.pslf0726, PSLF_0726_CREATED_AT),
    pslfLegacy: pslfLegacyRecord(selection),
  };
}

function page(data: JsonApiResource[]): JsonApiDoc {
  return { data, meta: { page: { total: data.length } } };
}

function singleRecommendation(selection: DemoSessionSelectionSnapshot, url: string): JsonApiDoc {
  const id = SINGLE_URL_RE.exec(url)?.[1] ?? '';
  const slug = RUN_ID_RE.exec(id)?.[1];
  let record: JsonApiResource | undefined;
  if (slug === 'idr-0726') {
    record = idr0726Record(selection, false);
  } else if (slug === 'sf-0726') {
    record = sf0726Record(selection);
  } else if (slug === 'pslf-0726') {
    record = pslf0726Record(selection, id);
  } else if (id === DEMO_LOAN_REC_IDS.idr0726Latest) {
    record = idr0726Record(selection, false);
  } else if (id === DEMO_LOAN_REC_IDS.idr0726Earlier) {
    record = idr0726Record(selection, true);
  } else if (id === DEMO_LOAN_REC_IDS.idrLegacy) {
    record = idrLegacyRecord(selection);
  } else if (id === DEMO_LOAN_REC_IDS.sf0726) {
    record = sf0726Record(selection);
  } else if (id === DEMO_LOAN_REC_IDS.pslf0726) {
    record = pslf0726Record(selection, id, PSLF_0726_CREATED_AT);
  } else if (id === DEMO_LOAN_REC_IDS.pslfLegacy) {
    record = pslfLegacyRecord(selection);
  }
  return { data: record ? { ...record, id } : null };
}

function collection(selection: DemoSessionSelectionSnapshot, app: string): JsonApiDoc {
  if (app === 'PSLF-0726' || app === 'PSLF') {
    if (!demoPslfHistoryVisible(selection)) {
      return page([]);
    }
    return page([
      app === 'PSLF'
        ? pslfLegacyRecord(selection)
        : pslf0726Record(selection, DEMO_LOAN_REC_IDS.pslf0726, PSLF_0726_CREATED_AT),
    ]);
  }
  if (!demoLoanHistoryVisible(selection)) {
    return page([]);
  }
  if (app === 'IDR-0726') {
    return page([idr0726Record(selection, false), idr0726Record(selection, true)]);
  }
  if (app === 'IDR') {
    return page([idrLegacyRecord(selection)]);
  }
  if (app === 'SF-0726') {
    return page([sf0726Record(selection)]);
  }
  return page([]);
}

function answerProfile(selection: DemoSessionSelectionSnapshot): JsonApiDoc {
  const household = sessionHousehold(selection);
  const firstLogin = isFirstLoginPersona(selection.userScenarioId);
  const returningApp = demoPslfActive(selection) ? 'PSLF-0726' : 'IDR-0726';
  return page([
    {
      type: 'answer-profile',
      id: 'demo-loan-answer-profile-1',
      attributes: {
        providingApp: firstLogin ? 'DASHBOARD' : returningApp,
        householdIncome: firstLogin ? null : household.agiCents,
        numberOfAdults: firstLogin ? null : household.numberOfAdults,
        numberOfChildren: firstLogin ? null : household.numberOfChildren,
        householdSize: firstLogin ? null : household.numberOfAdults + household.numberOfChildren,
        federalFirstLoanTime: firstLogin ? null : household.firstLoanTime,
        federalLastLoanTime: firstLogin ? null : household.lastLoanTime,
        madePayments: firstLogin ? null : household.madePayments,
        affordPayments: firstLogin ? null : household.affordPayments,
        recertifiedIncomeAt: null,
        changedRepaymentPlanAt: null,
        consolidatedLoansAt: null,
      },
      relationships: { user: { data: { type: 'user', id: USER_ID } } },
    },
  ]);
}

type PostedResource = {
  attributes?: Record<string, unknown>;
  relationships?: { user?: { data?: { id?: unknown } | null } };
};

function postedRecommendation(body: unknown) {
  const resource = (body as { data?: PostedResource } | null | undefined)?.data;
  const attributes = resource?.attributes;
  const app = attributes?.['requestingApp'] ?? attributes?.['requesting-app'];
  const input = (attributes?.['calculationInput'] ?? attributes?.['calculation-input']) as
    { data?: unknown } | undefined;
  const userId = resource?.relationships?.user?.data?.id;
  if (
    typeof app !== 'string' ||
    !ECHO_SLUGS[app] ||
    !Array.isArray(input?.data) ||
    (userId !== undefined && userId !== USER_ID)
  ) {
    return undefined;
  }
  return { app, rows: input.data as unknown[] };
}

function submitEcho(body: unknown): JsonApiDoc {
  const posted = postedRecommendation(body);
  if (!posted) {
    return { data: null };
  }
  const rows = posted.rows.map((row) =>
    row && typeof row === 'object' ? (row as SortPlansPayload) : ({} as SortPlansPayload)
  );
  submitSeq += 1;
  return {
    data: {
      type: 'recommendation',
      id: `demo-loan-rec-run-${ECHO_SLUGS[posted.app]}-${Date.now().toString(36)}-${submitSeq}`,
      // requestingApp and calculationInput are omitted so the submitted values survive the commit.
      attributes: {
        createdAt: new Date().toISOString(),
        hasErrorInCalcs: false,
        calculationOutput: { data: rows.map((row) => buildSortPlansOutput(row, posted.app)) },
      },
    },
  };
}

export function buildLoanEstimatorLayer(selection: DemoSessionSelectionSnapshot): {
  matchers: MatcherFixture[];
  fetchMatchers: FetchMatcher[];
} {
  const collectionMatcher = (app: string): FetchMatcher => ({
    urlMatches: (url) =>
      COLLECTION_URL_RE.test(url) && requestingAppIs(url, app) && isDemoUserFilter(url),
    method: 'GET',
    json: () => collection(selection, app),
  });
  return {
    matchers: [
      {
        matcher: { kind: 'builder', method: 'GET', urlMatches: (url) => SUMMARY_URL_RE.test(url) },
        contentFor: () => demoSessionLoanSummary(selection),
      },
      {
        matcher: {
          kind: 'builder',
          method: 'POST',
          urlMatches: (url) => GENERATE_URL_RE.test(url),
        },
        contentFor: (body) => simulateSchedule(body as GeneratePaymentPlanPayload | undefined),
      },
    ],
    fetchMatchers: [
      {
        urlMatches: (url) => SUMMARY_URL_RE.test(url),
        method: 'GET',
        json: () => demoSessionLoanSummary(selection),
      },
      {
        urlMatches: (url) => SINGLE_URL_RE.test(url),
        method: 'GET',
        json: (url: string) => singleRecommendation(selection, url),
      },
      collectionMatcher('IDR-0726'),
      collectionMatcher('IDR'),
      collectionMatcher('SF-0726'),
      collectionMatcher('STRATEGY_FINDER'),
      collectionMatcher('PSLF-0726'),
      collectionMatcher('PSLF'),
      {
        urlMatches: (url) => ANSWER_PROFILES_URL_RE.test(url) && isDemoUserFilter(url),
        method: 'GET',
        json: () => answerProfile(selection),
      },
      {
        urlMatches: (url) => CREATE_URL_RE.test(url),
        method: 'POST',
        status: 201,
        bodyMatches: (body) => postedRecommendation(body) !== undefined,
        json: (_url: string, body?: unknown) => submitEcho(body),
      },
    ],
  };
}
